import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../../hooks/useClickOutside';

export interface StationFilterOption {
  id: string;
  label: string;
}

export interface StationFilterDropdownProps {
  value: string[];
  options: readonly StationFilterOption[];
  onChange: (next: string[]) => void;
  ariaLabel: string;
  isLoading?: boolean;
}

function StationFilterDropdownInner({
  value,
  options,
  onChange,
  ariaLabel,
  isLoading,
}: StationFilterDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(ref, close, isOpen);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const buttonLabel = useMemo(() => {
    if (value.length === 0) return t('dashboard.filters.station.all');
    if (value.length === 1) {
      const match = options.find((o) => o.id === value[0]);
      return match?.label ?? value[0];
    }
    return t('dashboard.filters.station.nSelected', { count: value.length });
  }, [value, options, t]);

  const toggle = useCallback(
    (id: string) => {
      if (selectedSet.has(id)) {
        onChange(value.filter((v) => v !== id));
      } else {
        onChange([...value, id]);
      }
    },
    [selectedSet, onChange, value],
  );

  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <div className="status-filter-wrapper" ref={ref}>
      <button
        type="button"
        className={`status-filter-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${ariaLabel}: ${buttonLabel}`}
      >
        <span className="status-filter-icon-left" aria-hidden="true">
          <span className="material-symbols-outlined">ev_station</span>
        </span>
        <span>{buttonLabel}</span>
        <span className="status-filter-icon-right" aria-hidden="true">
          <span className="material-symbols-outlined">expand_more</span>
        </span>
      </button>

      {isOpen && (
        <div
          className="status-filter-dropdown station-filter-dropdown"
          role="listbox"
          aria-multiselectable="true"
          aria-label={ariaLabel}
        >
          <button
            type="button"
            role="option"
            aria-selected={value.length === 0}
            className={`status-filter-option ${value.length === 0 ? 'selected' : ''}`}
            onClick={clearAll}
          >
            <span className="station-filter-checkbox" aria-hidden="true">
              {value.length === 0 && (
                <span className="material-symbols-outlined">check</span>
              )}
            </span>
            <span>{t('dashboard.filters.station.all')}</span>
          </button>

          {isLoading && options.length === 0 ? (
            <div className="station-filter-empty">
              {t('dashboard.filters.station.loading')}
            </div>
          ) : options.length === 0 ? (
            <div className="station-filter-empty">
              {t('dashboard.filters.station.empty')}
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selectedSet.has(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`status-filter-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggle(option.id)}
                >
                  <span className="station-filter-checkbox" aria-hidden="true">
                    {isSelected && (
                      <span className="material-symbols-outlined">check</span>
                    )}
                  </span>
                  <span className="station-filter-option-label">{option.label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export const StationFilterDropdown = memo(StationFilterDropdownInner);
